import { useCallback, useEffect, useMemo, useRef } from "react";

import { isAxiosError } from "axios";
import { isValid, parseISO } from "date-fns";
import { isEqual } from "lodash-es";
import * as yup from "yup";

import {
  MAX_MULTILINE_TEXT_LENGTH,
  MAX_TEXT_LENGTH,
} from "src/cases/caseView/constants";
import { buildCaseActionFormSchema } from "src/cases/caseView/schema";
import { RunnningCaseAction } from "src/cases/caseView/types";
import { useDismissCaseAction } from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import {
  CaseTemplateActionOutput,
  CaseTemplateFormFieldsInner,
  CaseTemplateTaskOutput,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { assertUnreachable } from "src/utils/typeUtils";

export type CaseTaskFormValues = Record<string, unknown>;

const getBaseSchema = (
  field: CaseTemplateFormFieldsInner,
): yup.AnySchema | null => {
  switch (field.type) {
    case "text": {
      const maxLength = field.display_as_textarea
        ? MAX_MULTILINE_TEXT_LENGTH
        : MAX_TEXT_LENGTH;
      return yup
        .string()
        .max(
          maxLength,
          `${field.display_name} must be at most ${maxLength.toLocaleString()} characters`,
        );
    }
    case "number":
      return yup
        .number()
        .transform((_, originalValue) => {
          return originalValue === "" || originalValue === null
            ? null
            : Number(originalValue);
        })
        .typeError(`${field.display_name} must be a number`);

    case "integer":
      return yup
        .number()
        .transform((_, originalValue) => {
          return originalValue === "" || originalValue === null
            ? null
            : Number(originalValue);
        })
        .typeError(`${field.display_name} must be a number`)
        .integer(`${field.display_name} must be a non-decimal number`);

    case "select":
      return yup.string();
    case "multi_select":
      return yup.array().of(yup.string());
    case "date":
      return yup
        .mixed()
        .test(
          "is-valid-date",
          `${field.display_name} must be a valid date`,
          (value) => {
            if (!value || value === "") return true;
            // Handle both Date objects (from DateTimePickerDropdown) and strings (from backend)
            if (value instanceof Date) return isValid(value);
            if (typeof value === "string") return isValid(parseISO(value));
            return false;
          },
        )
        .transform((_, originalValue) => {
          if (originalValue === "" || !originalValue) return null;
          if (originalValue instanceof Date) {
            return originalValue.toISOString();
          }
          if (typeof originalValue === "string") {
            const date = parseISO(originalValue);
            return isValid(date) ? date.toISOString() : null;
          }
          return null;
        });
    default:
      assertUnreachable(field);
      return null;
  }
};

const buildCaseTaskFormSchema = (task?: CaseTemplateTaskOutput) => {
  if (!task?.form?.fields) return yup.object({});

  return yup.object(
    task.form.fields.reduce(
      (schema, field) => {
        const baseSchema = getBaseSchema(field);
        if (baseSchema) {
          // All fields are optional for form validation - required check happens only for checkbox enabling
          schema[field.key] = baseSchema.nullable();
        }
        return schema;
      },
      {} as Record<string, yup.AnySchema>,
    ),
  ) as yup.ObjectSchema<CaseTaskFormValues>;
};

export type AvailableCaseAction =
  CaseDeserialized["data"]["case_actions"][number];

/**
 * Get the available case actions based on the current case status and sub-status.
 */
export const useAvailableCaseActions = (
  caseData?: CaseDeserialized,
): AvailableCaseAction[] => {
  const { case_properties, case_actions } = caseData?.data ?? {};
  const { status, sub_status } = case_properties ?? {};

  return useMemo(() => {
    return (
      case_actions?.filter((action) =>
        isEqual(
          {
            status: action.status,
            sub_status: action.sub_status ?? null,
          },
          {
            status,
            sub_status: sub_status ?? null,
          },
        ),
      ) ?? []
    );
  }, [case_actions, status, sub_status]);
};

export const useGetCaseTemplateAction = (caseData?: CaseDeserialized) => {
  return useCallback(
    (actionKey?: string) => {
      if (!caseData || !actionKey) return undefined;
      return caseData?.data.template_version?.case_actions?.[actionKey];
    },
    [caseData],
  );
};

/**
 * Get schema for the selected case action form.
 */
export const useCaseActionFormSchema = (
  caseTemplateAction?: CaseTemplateActionOutput,
) =>
  useMemo(
    () => buildCaseActionFormSchema(caseTemplateAction),
    [caseTemplateAction],
  );

export type AvailableCaseTask = CaseDeserialized["data"]["case_tasks"][number];

/**
 * Get the available case tasks based on the current case status and sub-status.
 */
export const useAvailableCaseTasks = (
  caseData?: CaseDeserialized,
): AvailableCaseTask[] => {
  return useMemo(() => {
    const { case_properties, case_tasks } = caseData?.data ?? {};
    const { status, sub_status } = case_properties ?? {};

    if (!case_tasks || !status || !caseData?.data.id) return [];

    return case_tasks.filter((task) => {
      const taskIdParts = task.id.split("#");
      if (taskIdParts.length !== 3) return false;

      const [caseId, taskStatus] = taskIdParts;
      const expectedStatus = sub_status ? `${status}-${sub_status}` : status;

      return caseId === caseData.data.id && taskStatus === expectedStatus;
    });
  }, [caseData?.data]);
};

export const useGetCaseTemplateTask = (caseData?: CaseDeserialized) => {
  return useCallback(
    (taskKey?: string) => {
      if (!caseData || !taskKey) return undefined;
      return caseData?.data.template_version?.case_tasks?.[taskKey];
    },
    [caseData],
  );
};

/**
 * Hook to check if the Tasks tab should be visible based on available tasks and actions
 */
export const useShouldShowTasksTab = (caseData?: CaseDeserialized): boolean => {
  const tasks = useAvailableCaseTasks(caseData);
  const actions = useAvailableCaseActions(caseData);
  return tasks.length > 0 || actions.length > 0;
};

/**
 * Show toast notifications for the active case action.
 * Handles queued and failed states.
 */
export const useActiveCaseActionToast = (params: {
  activeAction?: RunnningCaseAction | null;
  caseEtag?: string;
  caseId?: string;
}) => {
  const activeToastRef = useRef<string | undefined>(undefined);
  const { activeAction, caseEtag, caseId } = params;

  const { mutateAsync: dismissCaseAction } = useDismissCaseAction(caseId);

  const handleDismiss = useCallback(async () => {
    if (!activeAction?.id || !caseId || !caseEtag) return;
    const toastId = activeToastRef.current;

    try {
      await dismissCaseAction({
        actionId: activeAction.id,
        etag: caseEtag,
      });

      toastActions.success({
        // Dismiss the failure toast if present
        id: toastId,
        title: "Action dismissed",
        description: "We won't show this to you anymore",
      });
    } catch (error) {
      logger.error(`Failed to dismiss case action: ${error}`);

      if (isPreconditionError(error)) {
        toastActions.failure({
          id: toastId,
          title: "Failed to dismiss action",
          description:
            "The case has been modified elsewhere. Please reload and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          id: toastId,
          title: "Failed to dismiss action",
          description: error.response?.data?.message,
        });
        return;
      }

      toastActions.failure({
        id: toastId,
        title: "Failed to dismiss action",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  }, [activeAction, caseEtag, dismissCaseAction, caseId]);

  useEffect(() => {
    // Dismiss any existing toasts before showing a new one
    if (activeToastRef.current) {
      toastActions.dismiss(activeToastRef.current);
    }

    const hasQueuedCaseCreationAction =
      activeAction?.type === "case_creation" &&
      activeAction.status === "queued";
    const hasFailedAction = activeAction?.status === "failed";

    if (hasFailedAction) {
      activeToastRef.current = toastActions.failure({
        title:
          activeAction?.type === "case_creation"
            ? "Case preparation flow failed"
            : "Action failed",
        duration: Infinity,
        description:
          activeAction?.error_message ??
          "The action could not be completed. Please try again.",
        actionText: "Dismiss",
        onActionClick: () => {
          toastActions.dismiss(activeToastRef.current ?? "");
          handleDismiss();
        },
      });
    } else if (hasQueuedCaseCreationAction) {
      activeToastRef.current = toastActions.loading({
        title: "Case is being prepared...",
        duration: Infinity,
        description:
          "We’re running the Case Preparation Flow to gather and structure all relevant case data.  This may take a few moments.",
      });
    } else {
      // Clean up toast ref if no relevant active action
      activeToastRef.current = undefined;
    }

    return () => {
      if (activeToastRef.current) {
        toastActions.dismiss(activeToastRef.current);
      }
    };
  }, [handleDismiss, activeAction]);
};

export const useCaseTaskFormSchema = (
  caseTemplateTask?: CaseTemplateTaskOutput,
) =>
  useMemo(() => buildCaseTaskFormSchema(caseTemplateTask), [caseTemplateTask]);

export const useTaskRequiredFieldsComplete = (
  templateTask?: CaseTemplateTaskOutput,
  formValues?: Record<string, unknown>,
): boolean => {
  return useMemo(() => {
    if (!templateTask?.form?.fields || !formValues) return false;

    const requiredFields = templateTask.form.fields.filter(
      (field) => field.required,
    );
    if (requiredFields.length === 0) return true;

    return requiredFields.every((field) => {
      const value = formValues[field.key];
      return (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      );
    });
  }, [templateTask?.form?.fields, formValues]);
};

export const useTaskFormValidForCompletion = (
  templateTask?: CaseTemplateTaskOutput,
  formValues?: Record<string, unknown>,
): boolean => {
  const requiredFieldsComplete = useTaskRequiredFieldsComplete(
    templateTask,
    formValues,
  );
  const taskFormSchema = useCaseTaskFormSchema(templateTask);

  return useMemo(() => {
    if (!requiredFieldsComplete || !formValues) return false;

    try {
      taskFormSchema.validateSync(formValues, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }, [requiredFieldsComplete, formValues, taskFormSchema]);
};

/**
 * Check if the case action form is valid based on the provided form values and action schema.
 */
export const useIsCaseActionFormValid = (
  caseTemplateAction?: CaseTemplateActionOutput,
  formValues?: Record<string, unknown>,
): boolean => {
  const actionFormSchema = useCaseActionFormSchema(caseTemplateAction);

  return useMemo(() => {
    if (!formValues) return false;

    try {
      actionFormSchema.validateSync(formValues, {
        abortEarly: false,
      });
      return true;
    } catch {
      return false;
    }
  }, [formValues, actionFormSchema]);
};
